import type { DropdownMenuItem } from '@nuxt/ui';
import type { MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';
import { useI18n } from 'vue-i18n';

interface CopyRawValueMenuItemOptions {
	/** Whether the value was just copied; toggles the label/icon to the "Copied" confirmation. */
	copied: MaybeRefOrGetter<boolean>;
	/** Copies the serialized value and flags the copied state. */
	onCopy: () => void;
	disabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Builds the shared "Copy raw value" dropdown menu item used by field label and cell context menus:
 * a label/icon that flips to a "Copied" confirmation, kept visible by preventing the menu from closing.
 */
export function useCopyRawValueMenuItem() {
	const { t } = useI18n();

	return function buildCopyRawValueMenuItem({ copied, onCopy, disabled }: CopyRawValueMenuItemOptions): DropdownMenuItem {
		return {
			label: toValue(copied) ? t('common.copied') : t('common.copy_raw_value'),
			icon: toValue(copied) ? 'lucide:check' : 'lucide:copy',
			// preventDefault keeps the menu open so the "Copied" confirmation stays visible.
			onSelect: (event) => {
				onCopy();
				event.preventDefault();
			},
			disabled: toValue(disabled),
		};
	};
}
